import type { Item } from "./infra";

export function normalizeDomain(value: unknown): string {
  return String(value || "").trim().toLowerCase().replace(/\.$/, "");
}

function matchesName(name: string, domain: string): boolean {
  name = normalizeDomain(name);
  return name === domain || (name.startsWith("*.") && domain.includes(".") && domain.slice(domain.indexOf(".") + 1) === name.slice(2));
}

export function apacheDomainRows(sites: Item[], certificates: Item[], records: Item[], now = Date.now()): Item[] {
  const groups = new Map<string, Item>();
  for (const site of sites) {
    const domains = site["domains"]?.length ? site["domains"].map(normalizeDomain) : [""];
    for (const domain of new Set<string>(domains)) {
      const key = domain || "file:" + site["name"];
      const row = groups.get(key) || { domain, sites: [] };
      if (!row["sites"].some((s: Item) => s["name"] === site["name"])) row["sites"].push(site);
      groups.set(key, row);
    }
  }
  return [...groups.values()].map(row => {
    const domain = row["domain"];
    const tlsSites = row["sites"].filter((site: Item) => !site["problem"] && (
      Array.isArray(site["tlsDomains"])
        ? site["tlsDomains"].some((name: string) => normalizeDomain(name) === domain)
        : site["tls"]
    ));
    const readable = row["sites"].filter((site: Item) => !site["problem"]);
    const primary = readable.find((site: Item) => !tlsSites.includes(site)) || readable[0] || null;
    const certificate = domain ? certificates.filter(c => (c["dnsNames"] || []).some((name: string) => matchesName(name, domain)))
      .sort((a, b) => Date.parse(b["expiresAt"]) - Date.parse(a["expiresAt"]))[0] || null : null;
    const record = domain ? records.find(r => r["managed"] && ["A", "AAAA", "CNAME"].includes(r["type"]) && !domain.includes("*") &&
      r["drift"] !== "externally-deleted" && normalizeDomain(r["name"]) === domain) || null : null;
    const expired = certificate && Date.parse(certificate["expiresAt"]) <= now;
    const tls = tlsSites.length > 0;
    const unknown = row["sites"].some((site: Item) => !!site["problem"]);
    return {
      ...row, primary, tlsSites, certificate, record,
      tlsLabel: expired ? "인증서 만료" : tls ? "TLS 설정됨" : certificate ? "인증서 발급됨" : unknown ? "확인 불가" : "TLS 미설정",
      tlsTone: expired ? "danger" : tls ? "success" : certificate || unknown ? "warning" : "neutral",
      tlsDetail: certificate ? (tls ? "앱 관리 Certbot 인증서" : "Apache TLS 설정을 확인하세요.") :
        tls ? "" : unknown ? "읽지 못한 설정 파일을 확인하세요." : "",
      certificateAction: certificate ? "Certbot 갱신" : tls ? "TLS 설정 확인" : "Certbot 발급",
      certificateActionEnabled: !!(certificate || tls || (record && !unknown)),
      certificateHint: unknown ? "설정 파일을 먼저 확인하세요." : "같은 도메인의 관리 DNS 레코드가 필요합니다.",
    };
  }).sort((a, b) => a["domain"].localeCompare(b["domain"]));
}
